import React, { useEffect, useState } from "react";
import { api, fileSrc, SPECIALTIES, specialtyColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Loader2, FileText, ShieldCheck, ExternalLink, HardHat, Users, IdCard, UserRound } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);

const empty = { name: "", contact_name: "", phone: "", email: "", specialties: [], ein: "", address: "", coi_url: "", w9_url: "", employees: [] };

export default function Subcontractors() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState("");

  const load = () => api.get("/subcontractors").then(setList);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    const specs = s.specialties?.length ? s.specialties : (s.specialty ? [s.specialty] : []);
    setForm({ ...empty, ...s, specialties: specs, employees: s.employees || [] });
    setOpen(true);
  };

  const toggleSpecialty = (sp) => {
    const cur = form.specialties || [];
    setForm({ ...form, specialties: cur.includes(sp) ? cur.filter((x) => x !== sp) : [...cur, sp] });
  };

  const uploadDoc = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const res = await api.upload(file);
      setForm((f) => ({ ...f, [key]: res.url }));
    } catch { toast.error(t("error")); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const uploadEmployeeOsha = async (e, idx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(`osha-${idx}`);
    try {
      const res = await api.upload(file);
      setForm((f) => {
        const emps = [...f.employees];
        emps[idx] = { ...emps[idx], osha_card_url: res.url };
        return { ...f, employees: emps };
      });
    } catch { toast.error(t("error")); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const setEmployee = (idx, patch) => {
    setForm((f) => {
      const emps = [...f.employees];
      emps[idx] = { ...emps[idx], ...patch };
      return { ...f, employees: emps };
    });
  };

  const addEmployee = () => setForm((f) => ({ ...f, employees: [...f.employees, { name: "", osha_card_url: "" }] }));
  const removeEmployee = (idx) => setForm((f) => ({ ...f, employees: f.employees.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("name"));
    const payload = { ...form, employees: (form.employees || []).filter((e) => e.name.trim()) };
    try {
      if (editing) await api.put(`/subcontractors/${editing.id}`, payload);
      else await api.post("/subcontractors", payload);
      toast.success(t("saved"));
      setOpen(false); load();
    } catch { toast.error(t("error")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDeleteSub"))) return;
    await api.del(`/subcontractors/${id}`);
    toast.success(t("deleted")); load();
  };

  const cardSpecs = (s) => (s.specialties?.length ? s.specialties : (s.specialty ? [s.specialty] : []));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("subcontractors")}</h1>
        <button data-testid="new-subcontractor-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
          <Plus className="w-4 h-4" /> {t("newSubcontractor")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} data-testid={`subcontractor-card-${s.id}`} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-white"><HardHat className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-semibold text-slate-900">{s.name}</h3>
                  {s.contact_name && <div className="text-xs text-slate-500 flex items-center gap-1"><UserRound className="w-3 h-3" />{s.contact_name}</div>}
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-subcontractor-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-subcontractor-${s.id}`} onClick={() => remove(s.id)} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {cardSpecs(s).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {cardSpecs(s).map((sp) => (
                  <span key={sp} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${specialtyColor(sp)}`}>{t(`sp_${sp}`) !== `sp_${sp}` ? t(`sp_${sp}`) : sp}</span>
                ))}
              </div>
            )}
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              {s.phone && <div>{s.phone}</div>}
              {s.email && <div className="truncate">{s.email}</div>}
              {s.ein && <div className="text-xs text-slate-400">EIN: {s.ein}</div>}
              {s.address && <div className="text-xs text-slate-400">{s.address}</div>}
            </div>
            {(s.employees || []).length > 0 && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{t("subEmployees")} ({s.employees.length})</div>
                <div className="space-y-1">
                  {s.employees.map((emp, i) => (
                    <div key={i} className="flex items-center justify-between text-sm text-slate-600">
                      <span className="truncate">{emp.name}</span>
                      {emp.osha_card_url ? (
                        <a href={fileSrc(emp.osha_card_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-medium shrink-0"><IdCard className="w-3 h-3" />OSHA</a>
                      ) : (
                        <span className="px-2 py-0.5 rounded-md bg-slate-50 text-slate-400 text-[11px] shrink-0">OSHA: {t("noFile")}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {s.coi_url ? (
                <a href={fileSrc(s.coi_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-medium"><ShieldCheck className="w-3.5 h-3.5" />COI <ExternalLink className="w-3 h-3" /></a>
              ) : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 text-slate-400">COI: {t("noFile")}</span>}
              {s.w9_url ? (
                <a href={fileSrc(s.w9_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 font-medium"><FileText className="w-3.5 h-3.5" />W-9 <ExternalLink className="w-3 h-3" /></a>
              ) : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 text-slate-400">W-9: {t("noFile")}</span>}
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-400 col-span-full py-12 text-center">{t("noData")}</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("subcontractors") : t("newSubcontractor")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("name")}>
                  <input data-testid="sub-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </Field>
                <Field label={t("contactName")}>
                  <input data-testid="sub-contact-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("phone")}><input data-testid="sub-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
                <Field label={t("email")}><input data-testid="sub-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
              </div>
              <Field label={t("specialtyAreas")}>
                <div className="flex flex-wrap gap-2">
                  {SPECIALTIES.map((sp) => {
                    const active = (form.specialties || []).includes(sp);
                    return (
                      <button key={sp} type="button" data-testid={`sub-specialty-${sp}`} onClick={() => toggleSpecialty(sp)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? "bg-slate-900 text-white" : `${specialtyColor(sp)} hover:opacity-80`}`}>
                        {t(`sp_${sp}`)}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("ein")}><input data-testid="sub-ein-input" value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} className={inputCls} /></Field>
                <Field label={t("address")}><input data-testid="sub-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Field>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500">{t("subEmployees")}</label>
                  <button type="button" data-testid="sub-add-employee-btn" onClick={addEmployee} className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                    <Plus className="w-3.5 h-3.5" /> {t("addEmployee")}
                  </button>
                </div>
                <div className="space-y-2">
                  {(form.employees || []).map((emp, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <input data-testid={`sub-employee-name-${idx}`} placeholder={t("name")} value={emp.name} onChange={(e) => setEmployee(idx, { name: e.target.value })} className={`${inputCls} bg-white flex-1`} />
                      {emp.osha_card_url ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={fileSrc(emp.osha_card_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium"><IdCard className="w-3.5 h-3.5" />OSHA</a>
                          <button type="button" onClick={() => setEmployee(idx, { osha_card_url: "" })} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-1 text-xs text-slate-600 border border-dashed border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-white cursor-pointer shrink-0">
                          {uploading === `osha-${idx}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <IdCard className="w-3.5 h-3.5" />} OSHA
                          <input data-testid={`sub-employee-osha-${idx}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadEmployeeOsha(e, idx)} />
                        </label>
                      )}
                      <button type="button" data-testid={`sub-remove-employee-${idx}`} onClick={() => removeEmployee(idx)} className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {(form.employees || []).length === 0 && <p className="text-xs text-slate-400">{t("noEmployeesYet")}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[["coi_url", t("coi")], ["w9_url", t("w9")]].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{label}</label>
                    {form[key] ? (
                      <div className="flex items-center gap-2">
                        <a href={fileSrc(form[key])} target="_blank" rel="noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1 flex-1 truncate"><FileText className="w-3.5 h-3.5" />{t("viewDoc")}</a>
                        <button onClick={() => setForm({ ...form, [key]: "" })} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-2 text-sm text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50 cursor-pointer w-full">
                        {uploading === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} {t("uploadFile")}
                        <input data-testid={`sub-${key}-input`} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(e, key)} />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="sub-save-btn" onClick={save} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
